/**
 * ProposalSummary Component
 *
 * Story FOS-4.2.7: VotingPage Implementation
 * Task 3: Create ProposalSummary component
 *
 * Condensed proposal view for voting page showing key details.
 *
 * @see AC-4.2.7.1 - Voting page shows proposal context
 *
 * @module components/governance/ProposalSummary
 */

import { Link } from 'react-router-dom';
import { ExternalLink, User, Calendar } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

export interface ProposalSummaryProps {
  /** Proposal ID */
  id: number;
  /** Proposal title */
  title: string;
  /** Proposal category */
  category: string;
  /** Short summary (truncated if > 200 chars) */
  summary: string;
  /** Proposer display name */
  proposerName: string;
  /** Creation timestamp (Unix milliseconds) */
  createdAt: number;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Truncate text to max length with ellipsis
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Format date for display using user's locale
 */
function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// =============================================================================
// Component
// =============================================================================

/**
 * Condensed proposal summary for voting page
 */
export function ProposalSummary({
  id,
  title,
  category,
  summary,
  proposerName,
  createdAt,
}: ProposalSummaryProps) {
  const truncatedSummary = truncateText(summary, 200);

  return (
    <div
      className="bg-muted/30 rounded-lg border border-border p-4"
      data-testid="proposal-summary"
    >
      {/* Header with category badge */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <span
            className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-primary/10 text-primary mb-2"
            data-testid="proposal-summary-category"
          >
            {category}
          </span>
          <h3
            className="font-medium text-foreground"
            data-testid="proposal-summary-title"
          >
            #{id}: {title}
          </h3>
        </div>
      </div>

      {/* Summary text */}
      <p
        className="text-sm text-muted-foreground mb-3"
        data-testid="proposal-summary-text"
      >
        {truncatedSummary}
      </p>

      {/* Meta info and link */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1" data-testid="proposal-summary-proposer">
            <User className="w-3 h-3" />
            {proposerName}
          </span>
          <span className="flex items-center gap-1" data-testid="proposal-summary-date">
            <Calendar className="w-3 h-3" />
            {formatDate(createdAt)}
          </span>
        </div>

        <Link
          to={`/proposals/${id}`}
          className="flex items-center gap-1 text-primary hover:underline"
          data-testid="proposal-summary-link"
        >
          Read full proposal
          <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

export default ProposalSummary;
